import { useMemo, Fragment, useContext } from "react"
import PropTypes from "prop-types"
import { map, omit, pick } from "lodash"
import { Divider } from "@mui/material"
import { createPeriodPerformanceSingleDateQuery, BATTING_TOTAL, BATTING_ALPHA, BATTING_FACTORS, SLUGGING_TOTAL, SLUGGING_ALPHA, SLUGGING_FACTORS } from "gatherers/single-date/period-performance"
import DataIntegrator from "gatherers/data-integrator"
import { smallNumberFormatter } from "helpers/formatters/numbers"
import CompareSection from "connected-components/compare/renderers/compare-section"
import { useModeInstruments } from "connected-components/dashboard/helpers"
import { useAnalytics } from "connected-components/analytics"
import { KPIContext } from "../../context/kpi"
import { KPI_EVENTS } from "../../analytics"
import { addDeltaColumn } from "../../helpers"
import { useIntradayMode } from "connected-components/dashboard/hooks/use-intraday-mode"

export default function KPIDecisions({ modelMetadata, context, instrumentsWithQueryOptions, instrumentsWithMetadata, getEffectiveEndDate }) {
  const { sectionSettings, setSectionExpanded } = useContext(KPIContext)
  const { base, selectedMode, performanceStartDate, isEndVsStartDateComparison } = useContext(context)
  const analytics = useAnalytics()

  const modelName = useMemo(() => pick(modelMetadata, ["id"]), [modelMetadata])

  const { original, rebalanced, comparison } = instrumentsWithQueryOptions
  // Skip delta in certain cases for decisions template
  const positionSets = useModeInstruments(original, rebalanced, comparison, selectedMode, true)
  const decisionsPositionSets = useMemo(() => map(positionSets, set => ({
    ...set,
    queryPositionSet: set,
    queryBase: base,
  })), [positionSets, base])

  // We ignore the base if we are in intraday mode to keep the queries consistent
  // since we also ignore the base for intraday simulation queries
  const [{ isIntradayMode }] = useIntradayMode()

  const decisionsSections = useMemo(
    () => [
      {
        id: "batting_total",
        name: "Batting - Total",
        defaultExpanded: true,
        separator: true,
        metrics: map(decisionsPositionSets, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            decisionsPositionSets[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              BATTING_TOTAL,
              BATTING_ALPHA,
              BATTING_FACTORS,
            ],
            { contextBase: base, isIntradayMode }),
        ),
        rowCount: 2,
      },
      {
        id: "slugging_total",
        name: "Slugging - Total",
        separator: true,
        metrics: map(decisionsPositionSets, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            decisionsPositionSets[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              SLUGGING_TOTAL,
              SLUGGING_ALPHA,
              SLUGGING_FACTORS,
            ],
            { contextBase: base, isIntradayMode }),
        ),
        rowCount: 2,
      },
    ],
    [decisionsPositionSets, modelName.id, performanceStartDate, getEffectiveEndDate, base, isIntradayMode],
  )

  const showDelta = positionSets.length > 1

  return map(decisionsSections, (section, index) => <Fragment key={`decisions-${section.id}`}>
    <DataIntegrator
      dataTransform={showDelta ? addDeltaColumn : undefined}
      metrics={section.metrics}
      Renderer={section.renderer || CompareSection}
      rendererProps={{
        queryState: sectionSettings?.[section.id] || { expanded: section.defaultExpanded, all: false },
        onQueryStateChange: (queryState) => {
          analytics.track(KPI_EVENTS.SECTION_TOGGLED, {
            sectionName: section.id,
            newState: queryState,
          })
          setSectionExpanded({ key: section.id, value: queryState })
        },
        index,
        formatter: smallNumberFormatter,
        configKey: `decisions-${section.id}`,
        columnCount: showDelta ? positionSets.length + 1 : positionSets.length,
        positionSetsMetadata: positionSets,
        instrumentsWithQueryOptions,
        instrumentsWithMetadata,
        isEndVsStartDateComparison,
        useSkeletonLoader: true,
        buttonSize: "small",
        ...omit(section, ["id", "metrics", "renderer"]),
      }}
    />
    {section.separator && <Divider />}
  </Fragment>)
}

KPIDecisions.propTypes = {
  modelMetadata: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    missingDataFiles: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })),
    availability: PropTypes.shape({
      securitiesStartDate: PropTypes.string,
      currentDate: PropTypes.string,
      unmodeledDates: PropTypes.object,
    }),
  }).isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  getEffectiveEndDate: PropTypes.func.isRequired,
}
